// Tracking Domain Management Only
// select an existing sending domain and add a new custom tracking domain to it
import React, { useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import axios from '~/data/http/axios'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import DataTable1 from '~/Common-Objects/Tables/DataTable1/DataTable1'
import FormError from '~/Common-Objects/UI/Forms/FormError/FormError'
import Hint from '~/Common-Objects/UI/Hint/Hint'
import Icon from '~/Common-Objects/UI/Icon'
import Icons from '~/Common-Objects/UI/Icons/index'
import Modal from '~/Common-Objects/Modal/index'
import Select from '~/Common-Objects/UI/Forms/Select/Select'
import SquareChip from '~/Common-Objects/UI/Chips/SquareChip.jsx'
import TextInput from '~/Common-Objects/UI/Forms/TextInput/TextInput'
import { alterSnackbar } from '~/data/actions/siteActions'
import copy from 'copy-to-clipboard'
import i18n from '~/i18n'
import TwoColPaperExplainer from './Components/TwoColPaperExplainer'

export default function TrackingDomainManagement({ finish = () => {}, existingSubdomain = null, slug = '' }) {
    const dispatch = useDispatch()
    const [providers, setProviders] = useState(null)
    const [sendingDomains, setSendingDomains] = useState(null)
    const [selectedSendingDomain, setSelectedSendingDomain] = useState(null)
    const [subdomain, setSubdomain] = useState(null)
    const [showInstructions, setShowInstructions] = useState(false)
    const [validating, setValidating] = useState(false)
    const [validated, setValidated] = useState(null)
    const [disableSubdomainInput, setDisableSubdomainInput] = useState(false)
    const [authError, setAuthError] = useState(false)
    const timer = useRef(null)
    const inputRef = useRef(null)

    const defaultProvider = {
        label: 'your service provider',
        value: 'NONE',
        icon: 'questionCircleOutline',
        iconColor: '#7C8086',
        instructions: [
            `<strong>Log in to your DNS provider’s account</strong> - This is typically your domain registrar or DNS host. If you’re not sure, you can use the DNS lookup tool <a href="https://who.is" target="_blank">who.is</a> to look up your domain.`,
            '<strong>Find your domain’s DNS settings</strong> - This may be under headings like DNS Settings, Zone Editor, or Advanced DNS.'
        ]
    }

    const getSendingDomains = async () => {
        axios
            .get('/accountMaster/domain')
            .then((res) => {
                setSendingDomains(res.data.domains)
                if (existingSubdomain) {
                    const sendingDomain = res.data.domains.find((d) => existingSubdomain.includes('.' + d.domainName))
                    if (sendingDomain) {
                        setSelectedSendingDomain(sendingDomain.id)
                        setSubdomain(existingSubdomain.replace('.' + sendingDomain.domainName, ''))
                        setShowInstructions(true)
                        setDisableSubdomainInput(true)
                        return
                    }
                }
                if (res.data.domains.length === 1) {
                    setSelectedSendingDomain(res.data.domains[0].id)
                }
            })
            .catch(() => {
                dispatch(alterSnackbar(true, 'Error fetching sending domains, please close and try again'))
            })
    }

    const copyVal = (val) => {
        copy(val)
        dispatch(alterSnackbar(true, 'Copied to clipboard'))
    }

    const setSubDomain = (e) => {
        const value = e.target.value
        setSubdomain(value)
        if (timer.current) {
            clearTimeout(timer.current)
        }
        timer.current = setTimeout(() => {
            setShowInstructions(isTrackingDomainValid(value))
        }, 200)
    }

    const validate = async () => {
        setValidating(true)
        try {
            const res = await axios.post('/accountMaster/tracking', {
                domainName: `${subdomain}.${sendingDomain.domainName}`
            })
            if (res.data.Domain.authenticated) {
                dispatch(alterSnackbar(true, 'Tracking Domain Validated'))
                finish(res.data.Domain.id)
            } else {
                setValidated(false)
                setAuthError('DNS entry not found. Check your DNS settings and try again')
            }
        } catch (err) {
            if (err.data?.info?.TrackingDomainId) {
                // attempt reauthentication
                await revalidate(err.data?.info?.TrackingDomainId)
            } else {
                setValidated(false)
                setAuthError(err.data?.error || 'An error occurred')
            }
        }
        setValidating(false)
    }

    const revalidate = async (trackingDomainId) => {
        try {
            const res = await axios.put(`/accountMaster/tracking/${trackingDomainId}/authenticate`)
            if (res.data.Domain.authenticated) {
                dispatch(alterSnackbar(true, 'Tracking Domain Validated'))
                finish(trackingDomainId)
            } else {
                setValidated(false)
                setAuthError('DNS entry not found. Check your DNS settings and try again')
            }
        } catch (err) {
            console.log(err)
        }
    }

    useEffect(() => {
        axios.get(`/accountMaster/domain/providers`).then((res) => {
            setProviders(res.data.providers)
        })
        getSendingDomains()

        return function () {
            if (timer.current) {
                clearTimeout(timer.current)
            }
        }
    }, [])

    useEffect(() => {
        if (selectedSendingDomain && inputRef.current) {
            inputRef.current.focus()
        }
    }, [selectedSendingDomain])

    if (!sendingDomains) return null

    const sendingDomain = selectedSendingDomain ? sendingDomains.find((d) => d.id === selectedSendingDomain) : null

    const provider = sendingDomain
        ? providers.find((p) => p.value === sendingDomain.serviceProvider) || defaultProvider
        : defaultProvider

    const newRecordsData = !!sendingDomain &&
        !!subdomain && [
            {
                type: {
                    headerValue: 'Type',
                    value: <SquareChip>CNAME</SquareChip>
                },
                host: {
                    headerValue: 'Host',
                    value: (
                        <TextInput
                            readOnly={true}
                            value={subdomain}
                            iconRight={
                                <Icon
                                    name='copy'
                                    onClick={() => {
                                        copyVal(subdomain)
                                    }}
                                />
                            }
                        />
                    )
                },
                value: {
                    headerValue: 'Value/Target',
                    value: (
                        <TextInput
                            readOnly={true}
                            value={'api.ourmailsender.com'}
                            iconRight={
                                <Icon
                                    name='copy'
                                    onClick={() => {
                                        copyVal('api.ourmailsender.com')
                                    }}
                                />
                            }
                        />
                    )
                }
            }
        ]

    let preview = ''
    if (slug && sendingDomain) {
        preview = 'https://' + (subdomain || '[subdomain]') + '.' + sendingDomain.domainName + '/s/' + slug
    }

    return (
        <Modal
            noClickAway={true}
            blurBg={true}
            top={false}
            closeButton={true}
            onCloseButton={() => finish()}
            noInnerPaperBorder={true}
        >
            <h3 className='mar-b20 text-center'>
                {disableSubdomainInput ? 'Authenticate Subdomain' : 'Add a new Subdomain'}
            </h3>
            {sendingDomains.length > 1 && !disableSubdomainInput && (
                <Select
                    outerClassName='mar-b10'
                    label='Select a Domain to add a new subdomain to'
                    data={sendingDomains.map((d) => ({ label: d.domainName, value: d.id }))}
                    value={selectedSendingDomain}
                    onChange={(e) => setSelectedSendingDomain(e.target.value)}
                />
            )}
            {!!selectedSendingDomain && !disableSubdomainInput && (
                <TextInput
                    inputRef={inputRef}
                    outerClassName='mar-b10'
                    label={`Enter a subdomain of your choice for ${sendingDomain.domainName}`}
                    placeholder='tracking'
                    value={subdomain}
                    onChange={setSubDomain}
                    explainer={
                        preview
                            ? `Preview: ${preview}`
                            : 'Something like "tracking", "analytics" or "pages" is a good choice.'
                    }
                />
            )}

            {!!showInstructions && (
                <>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        {i18n.t('company:domains.domainManagement.addRecord', {
                            provider: provider?.label
                        })}
                        {provider?.kbUrl && (
                            <Hint
                                href={provider?.kbUrl}
                                target='_blank'
                                style={{ margin: 0 }}
                                iconLeft={
                                    <Icons
                                        icon='questionCircle'
                                        color='#2B2F41'
                                        style={{ height: 16, width: 16 }}
                                    />
                                }
                            >
                                Learn more
                            </Hint>
                        )}
                    </div>
                    <DataTable1
                        tableData={newRecordsData}
                        loadingData={[]}
                        dataUpdater={() => {}}
                        width='100%'
                        pageSize='100'
                        hasCheckBoxes='no'
                        sortedColumn={''}
                        sortedDirection={''}
                        className='mar-b20'
                    />

                    <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
                        <Button
                            disabled={validating}
                            text='Validate Subdomain'
                            iconRight={validating ? <Icons icon='loading' /> : <Icons icon='arrowRight' />}
                            className='button-primary'
                            onClick={validate}
                        />

                        {validated !== null && <a onClick={() => finish()}>Close</a>}
                    </div>
                    {!!authError && <FormError>{authError}</FormError>}
                </>
            )}
            <TwoColPaperExplainer
                className='mar-t20 mar-b5'
                title={i18n.t('company:domains.domainManagement.trackingExplainerTitle')}
                content={i18n.t('company:domains.domainManagement.trackingExplainerContent2')}
            />
        </Modal>
    )
}

function isTrackingDomainValid(subdomain) {
    if (!subdomain) return false
    return subdomain.length > 0 && subdomain.length < 255 && !subdomain.includes('.')
}
